// api/ApiUser.ts

import axios, {Method} from 'axios';
import { refreshAccessToken } from './ApiAuth';
import { GetProfileRequest, RefreshTokenRequest } from './ApiRequest';
import { GetGamesHistoryRequest } from './ApiRequest';
import {ApiResponse, GameData, ProfileResponse, ReferralsDataResponse} from './ApiResponse';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

export async function apiUserRequest<T>(
    url: string,
    method: Method = 'POST',
    body?: any
): Promise<ApiResponse<T>> {
  try {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return { error: 'player-notification-server-error', statusCode: 400 };
    }

    const response = await axios.request<T>({
      url,
      method,
      data: body,
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    });

    // Помещаем данные сервера (например, { profile: ... }) в payload
    return {
      payload: response.data,
      statusCode: response.status,
    };
  } catch (error: any) {
    const response = error.response;
    if (response?.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        return { error: 'player-notification-server-error', statusCode: 400 };
      }
      const refreshBody: RefreshTokenRequest = { refreshToken };
      const refreshResult = await refreshAccessToken(refreshBody);
      if (refreshResult.payload?.accessToken) {
        localStorage.setItem('access_token', refreshResult.payload.accessToken);
        try {
          const retryResponse = await axios.request<T>({
            url,
            method,
            data: body,
            headers: {
              Authorization: `Bearer ${refreshResult.payload.accessToken}`,
              'Content-Type': 'application/json',
            },
          });
          return {
            payload: retryResponse.data,
            statusCode: retryResponse.status,
          };
        } catch (retryError: any) {
          const retryResp = retryError.response;
          return {
            error: retryResp?.data?.error || 'player-notification-server-error',
            message: retryResp?.data?.message || '',
            statusCode: retryResp?.status || 500,
          };
        }
      } else {
        return {
          error: refreshResult.error || 'Unable to refresh token',
          statusCode: refreshResult.statusCode,
        };
      }
    }
    return {
      error: response?.data?.error || 'player-notification-server-error',
      message: response?.data?.message || '',
      statusCode: response?.status || 500,
    };
  }
}

export const getProfile = async (
    body: GetProfileRequest
): Promise<ApiResponse<ProfileResponse>> => {

  const response = await apiUserRequest<{ profile: ProfileResponse }>(
      `${API_URL}/user/profile`,
      'POST',
      body
  );

  if (response.payload?.profile) {
    return {
      payload: response.payload.profile,
      statusCode: response.statusCode || 200,
    };
  }

  return {
    error: response.error || 'player-notification-server-error',
    message: response.message || 'Не удалось получить профиль',
    statusCode: response.statusCode || 500,
  };
};

export const getReferrals = async (): Promise<ApiResponse<ReferralsDataResponse>> => {
  return await apiUserRequest<ReferralsDataResponse>(
      `${API_URL}/user/referrals`,
      'POST'
  );
}


export const getGameHistory = async (
  body: GetGamesHistoryRequest
): Promise<ApiResponse<GameData[]>> => {

  const response = await apiUserRequest<{ games: GameData[] }>(
    `${API_URL}/user/history-games`,
    'POST',
    body
  );
  if (response.payload && response.payload.games) {
    return {
      payload: response.payload.games,
      statusCode: response.statusCode || 200,
    };
  }
  return {
    error: response.error || 'player-notification-server-error',
    message: response.message || 'Не удалось получить историю игр',
    statusCode: response.statusCode || 500,
  };
};
